/**
 * ─── Arena Schema — Types & Zod Schemas ─────────────────────
 * Fighting Arena Management System with membership, betting,
 * and entrance management.
 *
 * Entities:
 *  - Arena         — Venue/fighting pit configuration
 *  - Fighter       — Participating entries (with breed, weight, record)
 *  - Match         — Scheduled fights between two fighters
 *  - ArenaMember   — Membership with tiers and entrance privileges
 *  - Bet           — Wagering on matches with odds and payout
 *  - Entrance      — Entrance fees and ticket management
 *
 * Double-Entry: Bets and winnings flow through ledger entries.
 */

import { z } from 'zod'
import { notesSchema, createQuerySchema, createUpdateSchema } from '@repo/core'

// ═══════════════════════════════════════════════════════════════
// Arena
// ═══════════════════════════════════════════════════════════════

export type ArenaStatus = 'active' | 'inactive' | 'maintenance' | 'closed'

export interface Arena {
  id: string
  tenantId: string
  branchId?: string
  arenaCode: string
  name: string
  location: string
  capacity: number
  description?: string
  status: ArenaStatus
  operatingHours?: string
  contactNumber?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const ArenaStatusSchema = z.enum(['active', 'inactive', 'maintenance', 'closed'])

export const CreateArenaSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  arenaCode: z.string().min(1).max(20),
  name: z.string().min(1).max(200),
  location: z.string().min(1).max(500),
  capacity: z.number().int().min(1).max(100000),
  description: z.string().max(2000).optional(),
  status: ArenaStatusSchema.default('active'),
  operatingHours: z.string().max(100).optional(),
  contactNumber: z.string().max(50).optional(),
})

export const ARENA_STATUS_LABELS: Record<ArenaStatus, string> = {
  active: 'Active', inactive: 'Inactive', maintenance: 'Maintenance', closed: 'Closed',
}

// ═══════════════════════════════════════════════════════════════
// Fighter
// ═══════════════════════════════════════════════════════════════

export type FighterStatus = 'active' | 'injured' | 'retired' | 'suspended' | 'deceased'
export type WeightClass = 'lightweight' | 'welterweight' | 'middleweight' | 'heavyweight' | 'open'

export interface Fighter {
  id: string
  tenantId: string
  branchId?: string
  fighterCode: string
  name: string
  breed: string
  ownerName: string
  ownerContact?: string
  weight: number               // in grams/kilograms
  weightClass: WeightClass
  age: number                  // in months
  color?: string
  wins: number
  losses: number
  draws: number
  status: FighterStatus
  notes?: string
  photoUrl?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const FighterStatusSchema = z.enum(['active', 'injured', 'retired', 'suspended', 'deceased'])
export const WeightClassSchema = z.enum(['lightweight', 'welterweight', 'middleweight', 'heavyweight', 'open'])

export const CreateFighterSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  fighterCode: z.string().min(1).max(20),
  name: z.string().min(1).max(200),
  breed: z.string().min(1).max(100),
  ownerName: z.string().min(1).max(200),
  ownerContact: z.string().max(50).optional(),
  weight: z.number().min(0),
  weightClass: WeightClassSchema.default('open'),
  age: z.number().int().min(0),
  color: z.string().max(50).optional(),
  status: FighterStatusSchema.default('active'),
  notes: notesSchema.optional(),
})

export const FIGHTER_STATUS_LABELS: Record<FighterStatus, string> = {
  active: 'Active', injured: 'Injured', retired: 'Retired', suspended: 'Suspended', deceased: 'Deceased',
}
export const WEIGHT_CLASS_LABELS: Record<WeightClass, string> = {
  lightweight: 'Lightweight', welterweight: 'Welterweight', middleweight: 'Middleweight',
  heavyweight: 'Heavyweight', open: 'Open Class',
}

// ═══════════════════════════════════════════════════════════════
// Match / Event
// ═══════════════════════════════════════════════════════════════

export type MatchStatus = 'scheduled' | 'ongoing' | 'completed' | 'cancelled' | 'postponed'
export type MatchResult = 'fighter1_win' | 'fighter2_win' | 'draw' | 'no_contest'

export interface Match {
  id: string
  tenantId: string
  arenaId: string
  arenaName?: string
  fighter1Id: string
  fighter1Name?: string
  fighter2Id: string
  fighter2Name?: string
  matchCode: string
  scheduledDate: number          // timestamp
  weightClass: WeightClass
  status: MatchStatus
  result?: MatchResult
  winnerId?: string
  duration?: number              // in minutes
  roundCount?: number
  notes?: string
  referee?: string

  // Betting
  totalBetAmount: number
  fighter1Odds: number           // decimal odds
  fighter2Odds: number
  drawOdds: number
  houseCommission: number        // percentage (e.g. 0.05 = 5%)

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const MatchStatusSchema = z.enum(['scheduled', 'ongoing', 'completed', 'cancelled', 'postponed'])
export const MatchResultSchema = z.enum(['fighter1_win', 'fighter2_win', 'draw', 'no_contest'])

export const CreateMatchSchema = z.object({
  tenantId: z.string().min(1),
  arenaId: z.string().min(1),
  fighter1Id: z.string().min(1),
  fighter2Id: z.string().min(1),
  matchCode: z.string().min(1).max(20),
  scheduledDate: z.number(),
  weightClass: WeightClassSchema.default('open'),
  fighter1Odds: z.number().min(1).default(2.0),
  fighter2Odds: z.number().min(1).default(2.0),
  drawOdds: z.number().min(1).default(5.0),
  houseCommission: z.number().min(0).max(0.5).default(0.05),
  referee: z.string().max(200).optional(),
  notes: notesSchema.optional(),
})

export const MATCH_STATUS_LABELS: Record<MatchStatus, string> = {
  scheduled: 'Scheduled', ongoing: 'Ongoing', completed: 'Completed',
  cancelled: 'Cancelled', postponed: 'Postponed',
}
export const MATCH_RESULT_LABELS: Record<MatchResult, string> = {
  fighter1_win: 'Fighter 1 Wins', fighter2_win: 'Fighter 2 Wins',
  draw: 'Draw', no_contest: 'No Contest',
}

// ═══════════════════════════════════════════════════════════════
// Arena Member
// ═══════════════════════════════════════════════════════════════

export type MembershipTier = 'regular' | 'silver' | 'gold' | 'platinum' | 'vip'
export type MemberStatus = 'active' | 'suspended' | 'expired' | 'banned'

export interface ArenaMember {
  id: string
  tenantId: string
  branchId?: string
  memberCode: string
  firstName: string
  lastName: string
  fullName: string
  phone: string
  email?: string
  address?: string
  membershipTier: MembershipTier
  status: MemberStatus
  joinDate: number
  expiryDate: number
  totalBetsPlaced: number
  totalWinnings: number
  totalEntrances: number
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const MembershipTierSchema = z.enum(['regular', 'silver', 'gold', 'platinum', 'vip'])
export const MemberStatusSchema = z.enum(['active', 'suspended', 'expired', 'banned'])

export const CreateArenaMemberSchema = z.object({
  tenantId: z.string().min(1),
  branchId: z.string().optional(),
  memberCode: z.string().min(1).max(20),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  phone: z.string().min(1).max(50),
  email: z.string().email().optional(),
  address: z.string().max(500).optional(),
  membershipTier: MembershipTierSchema.default('regular'),
  joinDate: z.number().default(() => Date.now()),
  expiryDate: z.number().default(() => Date.now() + 365 * 86400000),
  notes: notesSchema.optional(),
})

export const MEMBERSHIP_TIER_LABELS: Record<MembershipTier, string> = {
  regular: 'Regular', silver: 'Silver', gold: 'Gold', platinum: 'Platinum', vip: 'VIP',
}
export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  active: 'Active', suspended: 'Suspended', expired: 'Expired', banned: 'Banned',
}

// ═══════════════════════════════════════════════════════════════
// Bet / Wager
// ═══════════════════════════════════════════════════════════════

export type BetStatus = 'placed' | 'won' | 'lost' | 'paid' | 'cancelled' | 'void'
export type BetType = 'fighter1' | 'fighter2' | 'draw'

export interface Bet {
  id: string
  tenantId: string
  matchId: string
  matchCode?: string
  memberId: string
  memberName?: string
  fighterId: string             // which fighter the bet is on (or 'draw')
  fighterName?: string
  betType: BetType
  amount: number
  odds: number                  // odds at time of bet
  potentialPayout: number       // amount * odds
  actualPayout?: number         // what was actually paid
  status: BetStatus
  placedAt: number
  settledAt?: number
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const BetStatusSchema = z.enum(['placed', 'won', 'lost', 'paid', 'cancelled', 'void'])
export const BetTypeSchema = z.enum(['fighter1', 'fighter2', 'draw'])

export const CreateBetSchema = z.object({
  tenantId: z.string().min(1),
  matchId: z.string().min(1),
  memberId: z.string().min(1),
  fighterId: z.string().min(1),
  betType: BetTypeSchema,
  amount: z.number().min(1),
  odds: z.number().min(1),
  potentialPayout: z.number().min(0),
})

export const BET_STATUS_LABELS: Record<BetStatus, string> = {
  placed: 'Placed', won: 'Won', lost: 'Lost', paid: 'Paid', cancelled: 'Cancelled', void: 'Void',
}
export const BET_TYPE_LABELS: Record<BetType, string> = {
  fighter1: 'Fighter 1', fighter2: 'Fighter 2', draw: 'Draw',
}

// ═══════════════════════════════════════════════════════════════
// Entrance
// ═══════════════════════════════════════════════════════════════

export type TicketType = 'general' | 'ringside' | 'vip_box' | 'complimentary'
export type PaymentStatus = 'pending' | 'paid' | 'refunded'

export interface Entrance {
  id: string
  tenantId: string
  matchId: string
  matchCode?: string
  memberId?: string
  memberName?: string
  guestName?: string            // for non-member guests
  ticketType: TicketType
  ticketPrice: number
  quantity: number
  totalAmount: number
  paymentStatus: PaymentStatus
  paymentMethod?: string
  entryTime?: number
  notes?: string

  createdAt: number
  updatedAt: number
  deletedAt: number | null
  version: number
  createdBy: string
  updatedBy: string
}

export const TicketTypeSchema = z.enum(['general', 'ringside', 'vip_box', 'complimentary'])
export const PaymentStatusSchema = z.enum(['pending', 'paid', 'refunded'])

export const CreateEntranceSchema = z.object({
  tenantId: z.string().min(1),
  matchId: z.string().min(1),
  memberId: z.string().optional(),
  guestName: z.string().max(200).optional(),
  ticketType: TicketTypeSchema.default('general'),
  ticketPrice: z.number().min(0),
  quantity: z.number().int().min(1).default(1),
  totalAmount: z.number().min(0),
  paymentMethod: z.string().max(50).optional(),
  notes: notesSchema.optional(),
})

export const TICKET_TYPE_LABELS: Record<TicketType, string> = {
  general: 'General Admission', ringside: 'Ringside', vip_box: 'VIP Box', complimentary: 'Complimentary',
}
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  pending: 'Pending', paid: 'Paid', refunded: 'Refunded',
}

// ═══════════════════════════════════════════════════════════════
// Query & Update Schemas
// ═══════════════════════════════════════════════════════════════

export const ArenaQuerySchema = createQuerySchema({})
export const FighterQuerySchema = createQuerySchema({})
export const MatchQuerySchema = createQuerySchema({})
export const ArenaMemberQuerySchema = createQuerySchema({})
export const BetQuerySchema = createQuerySchema({})
export const EntranceQuerySchema = createQuerySchema({})

export const UpdateArenaSchema = createUpdateSchema({})
export const UpdateFighterSchema = createUpdateSchema({})
export const UpdateMatchSchema = createUpdateSchema({})
export const UpdateArenaMemberSchema = createUpdateSchema({})
